import { computed } from 'vue';
import { SpacesAvailableModeEnum } from '@/epos/vue/pages/today/SpacesAvailableModeEnum';
import { useStore } from 'vuex';

/**
 * @typedef {import('@/epos/vue/modals/TodayViewFilters/types.js').FilterValues} FilterValues
 */

/**
 * Creates a filter object, optionally merging with provided custom default filters.
 *
 * @param {Partial<FilterValues>} [defaultFilters={}] - An optional partial object of FilterValues
 *
 * @returns {FilterValues}
 */
export const createDefaultFilterValues = (defaultFilters = {}) => {
    /** @type {FilterValues} */
    const baseDefaults = {
        categoryIDs: [],
        eventIDs: [],
        hideInactiveCategoriesAndEvents: false,
        hideSoldOut: false,
        itemBranchID: null,
        status: null,
        spacesAvailable: 0,
        spacesAvailableMode: SpacesAvailableModeEnum.MIN,
    };

    return Object.assign(baseDefaults, defaultFilters);
};

export const getSelectedFilterKeys = (filters) => {
    const defaults = createDefaultFilterValues();
    const selectedKeys = [];
    Object.keys(filters).forEach((key) => {
        if (filters[key] !== defaults[key]) {
            if (key === 'itemBranchID' && filters.itemBranchID && isDefaultItemBranch(filters.itemBranchID)) {
                return;
            }
            if (key === 'eventIDs' && Array.isArray(filters.eventIDs) && filters.eventIDs.length === 0) {
                return;
            }
            if (key === 'categoryIDs' && Array.isArray(filters.categoryIDs) && filters.categoryIDs.length === 0) {
                return;
            }
            selectedKeys.push(key);
        }
    });

    return selectedKeys;
};

const isDefaultItemBranch = (itemBranchID) => {
    const store = useStore();
    const currentBranch = computed(() => store.state.currentBranch);

    return currentBranch?.value?.ID === itemBranchID;
};

/**
 * @param filters
 * @return {number}
 */
export const getSelectedFilterCount = (filters) => {
    const selectedFilters = getSelectedFilterKeys(filters).filter((key) => {
        return ![
            // There always has to be an itemBranchID selected, but we don't always want to count it.
            'itemBranchID',
        ].includes(key);
    });

    let count = selectedFilters.length;

    if (filters.itemBranchID && !isDefaultItemBranch(filters.itemBranchID)) {
        // Only count itemBranchID as a filter if the item branch is different from the branch.
        // TODO: we should indicate that the item branch !== branch visually.
        count++;
    }

    // If both spacesAvailable and spacesAvailableMode have been modified, reduce the count by one because to
    // the user this is the same filter.
    if (selectedFilters.includes('spacesAvailable') && selectedFilters.includes('spacesAvailableMode')) {
        count -= 1;
    }

    return count;
};

export function createTodayStore() {
    return {
        namespaced: true,
        state: {
            // Last selected date on the today view.
            date: new Date(),

            filters: createDefaultFilterValues(),

            /**
             * An object of all events.
             *
             * @type {Object<number, Event>}
             */
            events: null,

            /**
             * An array of eventIDs filtered by selected events and selected categories.
             *
             * @type {number[]}
             */
            filteredEventIDs: [],

            // An object of all categories
            // TODO: could this just be an array?
            categories: null,

            // Array of sessions for the selected date.
            sessions: null,

            // Each key is an eventID and the value is an array of ticketIDs that exist for that event.
            ticketsByEvent: {},

            // Number of sessions on the selected day, before any filters are applied.
            sessionCountBeforeFilters: null,

            // Number of sessions on the selected day, before any filters are applied.
            sessionCount: null,

            // A list of hours that have a session in them.
            // Keys are hours (0 - 23) and values are the number of sessions in that hour.
            sessionHours: {},

            // Latest space counts for the displayed sessions.
            sessionAvailabilities: {},

            // The session.ID of the active popover.
            activeLinearPopover: null,
        },
        mutations: {
            setDate(state, date) {
                state.date = date;
            },

            setFilters(state, filters) {
                state.filters = filters;
            },

            setCategories(state, categories) {
                state.categories = categories;
            },

            setEvents(state, events) {
                state.events = events;
            },

            setSessions(state, { sessions, sessionAvailabilities, sessionHours }) {
                state.sessions = sessions;
                state.sessionAvailabilities = sessionAvailabilities;
                state.sessionHours = sessionHours;
                state.sessionCount = sessions.length;
            },

            setSessionCountBeforeFilters(state, count) {
                state.sessionCountBeforeFilters = count;
            },

            clearSessions(state) {
                state.sessions = null;
                state.sessionAvailabilities = {};
                state.sessionHours = {};
                state.sessionCount = null;
                state.sessionCountBeforeFilters = null;
            },

            setTicketsByEvent(state, ticketsByEvent) {
                state.ticketsByEvent = ticketsByEvent;
            },

            setFilteredEventIDs(state, eventIDs) {
                state.filteredEventIDs = eventIDs;
            },

            /**
             * @param state
             * @param {{sessionID: number}|null} data
             */
            setActiveLinearPopover(state, data) {
                state.activeLinearPopover = data;
            },
        },
        getters: {
            isLinearPopoverActive: (state) => (sessionID) => {
                return state.activeLinearPopover?.sessionID === sessionID;
            },
            sessionAvailability: (state) => (sessionID) => {
                return state.sessionAvailabilities[sessionID];
            },
            getEvent: (state) => (eventID) => {
                return state.events[eventID];
            },
        },
    };
}
